// 手机模式播放页 —— 完整复刻 Android 截图（视频 + 日期 + 时间轴 + 6 功能按钮）
// 路由：/m/player?storeId=&storeName=&deviceSn=&deviceLabel=&channel=
// 不接录像接口前，时间轴用 mock 区间 + 当前游标占位。
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { App as AntdApp, Button, Drawer, Empty, Spin, Typography } from 'antd';
import {
	LeftOutlined,
	PhoneOutlined,
	ShareAltOutlined,
	CaretDownOutlined,
	PauseCircleFilled,
	PlayCircleFilled,
	SoundOutlined,
	AudioMutedOutlined,
	FullscreenOutlined,
	FullscreenExitOutlined,
	CalendarOutlined,
	CameraOutlined,
	AudioOutlined,
	VideoCameraOutlined,
	EditOutlined,
	CompassOutlined,
	FileSearchOutlined,
	CheckCircleFilled,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import UluVideoPlayer, { type UluVideoPlayerHandle } from '../components/UluVideoPlayer.js';
import VideoTimeline from '../components/VideoTimeline.js';
import PtzControls from '../components/PtzControls.js';
import { callInternal } from '../api/internal.js';
import { fetchCloudHistory, fetchPlaybackUrl, type CloudSegment } from '../api/cloud-history.js';
import { fetchPlayToken, tokenDeviceId, type PlayTokenData } from '../api/play-token.js';
import { fetchDeviceCapabilities, FN, type DeviceCapabilities } from '../api/device-capabilities.js';
import { deviceLabel as formatDeviceLabel } from '../utils/device-label.js';
import { pushRecentRecord, captureVideoThumb } from '../utils/recent-records.js';
import { lockLandscape, unlockOrientation } from '../utils/native-orientation.js';
import { isCapacitor } from '../utils/api-base.js';
import { capabilityForModule, capabilityState, type CapabilityState } from '../capabilities.js';
import { useRegion } from '../hooks/useRegion.js';

const PRIMARY = '#FF7043';
const PAGE_BG = '#F2F3F7';

function isCanvasMostlyBlank(canvas: HTMLCanvasElement): boolean {
	try {
		const sample = document.createElement('canvas');
		sample.width = 16;
		sample.height = 16;
		const ctx = sample.getContext('2d');
		if (!ctx) return false;
		ctx.drawImage(canvas, 0, 0, 16, 16);
		const data = ctx.getImageData(0, 0, 16, 16).data;
		let blank = 0;
		let total = 0;
		for (let i = 0; i < data.length; i += 4) {
			const alpha = data[i + 3] ?? 255;
			const brightness = (data[i] ?? 0) + (data[i + 1] ?? 0) + (data[i + 2] ?? 0);
			if (alpha < 8 || brightness < 24) blank += 1;
			total += 1;
		}
		return total > 0 && blank / total > 0.96;
	} catch {
		return false;
	}
}

interface DeviceLite {
	device_id: number | string;
	sn?: string;
	name?: string;
	store_id?: string;
	status?: number | string;
	type_id?: number | string;
}

interface StoreContact {
	store_id: string | number;
	store?: string;
	store_phone?: string;
	shopowner_phone?: string;
	more_phone?: string[] | string;
}

interface DateChip {
	key: string;
	label: string;
}

type CapacitorSharePlugin = {
	share: (options: {
		title?: string;
		text?: string;
		url?: string;
		files?: string[];
		dialogTitle?: string;
	}) => Promise<void>;
};

type CapacitorFilesystemPlugin = {
	writeFile: (options: {
		path: string;
		data: string;
		directory: 'CACHE' | 'DOCUMENTS' | 'DATA';
		recursive?: boolean;
	}) => Promise<{ uri: string }>;
};

type NativeRecordingState = {
	recording: boolean;
	startedAt?: number;
	filename?: string;
	uri?: string;
	path?: string;
	durationMs?: number;
	size?: number;
};

type CapacitorScreenRecorderPlugin = {
	isAvailable: () => Promise<{ available: boolean; platform?: string; reason?: string }>;
	start: (options: { filename?: string; title?: string }) => Promise<NativeRecordingState>;
	stop: () => Promise<NativeRecordingState>;
	getState: () => Promise<NativeRecordingState>;
};

type CapacitorGlobal = {
	Plugins?: {
		Share?: CapacitorSharePlugin;
		Filesystem?: CapacitorFilesystemPlugin;
		ScreenRecorder?: CapacitorScreenRecorderPlugin;
	};
};

const buildDateChips = (count = 7): DateChip[] => {
	const out: DateChip[] = [];
	for (let i = 0; i < count; i++) {
		const d = dayjs().subtract(i, 'day');
		out.push({ key: d.format('YYYY-MM-DD'), label: d.format('MM-DD') });
	}
	return out;
};

function parsePlaybackAt(value: string): number {
	let text = value.trim();
	try {
		text = decodeURIComponent(text).trim();
	} catch {
		// URLSearchParams already decodes normal query values; keep the raw text on malformed escapes.
	}
	if (!text) return NaN;
	if (/^\d+$/.test(text)) {
		const parsed = Number(text);
		return parsed > 100000000000 ? Math.floor(parsed / 1000) : parsed;
	}
	const normalized = text.replace('T', ' ').replace(/-/g, '/');
	const parsed = dayjs(normalized);
	return parsed.isValid() ? parsed.unix() : NaN;
}

function findPlaybackSegment(segments: CloudSegment[], atUtc: number): CloudSegment | null {
	if (!segments.length) return null;
	const exact = segments.find((segment) => atUtc >= segment.start_utc && atUtc <= segment.end_utc);
	if (exact) return exact;
	let best: CloudSegment | null = null;
	let bestDistance = Infinity;
	for (const segment of segments) {
		const distance = atUtc < segment.start_utc
			? segment.start_utc - atUtc
			: atUtc - segment.end_utc;
		if (distance < bestDistance) {
			best = segment;
			bestDistance = distance;
		}
	}
	return best;
}

function uniqueText(values: Array<string | null | undefined>): string[] {
	const out: string[] = [];
	const seen = new Set<string>();
	for (const value of values) {
		const text = String(value ?? '').trim();
		if (!text || seen.has(text)) continue;
		seen.add(text);
		out.push(text);
	}
	return out;
}

export default function PlayerTab() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { message } = AntdApp.useApp();
	const [params, setParams] = useSearchParams();
	const region = useRegion();
	const isApac = region === 'apac';

	const storeId = params.get('storeId') ?? '';
	const deviceSn = params.get('deviceSn') ?? '';
	const deviceAutoId = params.get('deviceAutoId') ?? '';
	const historyDeviceIdParam = params.get('historyDeviceId') ?? '';
	const playDeviceIdParam = params.get('playDeviceId') ?? '';
	const channel = Number(params.get('channel') ?? 1);
	const storeName = params.get('storeName') ?? '';
	const deviceLabel = params.get('deviceLabel') ?? deviceSn;
	const hasDevice = Boolean(deviceSn);
	const initialMode = params.get('mode') ?? '';
	const initialDate = params.get('date') ?? '';
	const initialAt = params.get('at') ?? '';

	const playerRef = useRef<UluVideoPlayerHandle>(null);
	const autoPlaybackKeyRef = useRef('');
	const [paused, setPaused] = useState(false);
	const [muted, setMuted] = useState(false);
	const [quality, setQuality] = useState<'sd' | 'hd'>('hd');
	const [rotation, setRotation] = useState(0);
	const [speed, setSpeed] = useState<0.5 | 1 | 2 | 4>(1);
	const [filled, setFilled] = useState(false);
	const [status, setStatus] = useState<'idle' | 'loading' | 'playing' | 'error'>('idle');
	const [errMsg, setErrMsg] = useState<string | null>(null);

	const [storeDevices, setStoreDevices] = useState<DeviceLite[] | null>(null);
	const [storeContact, setStoreContact] = useState<StoreContact | null>(null);
	const [storeContactError, setStoreContactError] = useState<string | null>(null);
	const [switcherOpen, setSwitcherOpen] = useState(false);
	const [phoneOpen, setPhoneOpen] = useState(false);
	const [ptzOpen, setPtzOpen] = useState(false);
	const [shareFallbackText, setShareFallbackText] = useState<string | null>(null);
	const [recording, setRecording] = useState(false);
	const [recordingMode, setRecordingMode] = useState<'web' | 'native' | null>(null);
	const [recordStartedAt, setRecordStartedAt] = useState<number | null>(null);
	const [recordElapsedMs, setRecordElapsedMs] = useState(0);
	const recorderRef = useRef<MediaRecorder | null>(null);
	const recordChunksRef = useRef<Blob[]>([]);
	const recentSeedKeyRef = useRef('');

	// 全屏：包视频区的容器 ref + 全屏状态（用于显隐自定义退出按钮）
	const fsContainerRef = useRef<HTMLDivElement>(null);
	const [isFs, setIsFs] = useState(false);
	const nativeCleanRecording = recording && recordingMode === 'native';

	const dateChips = useMemo(buildDateChips, []);
	const [selectedDate, setSelectedDate] = useState<'live' | 'all' | string>(() => (
		initialMode === 'playback' && initialDate ? initialDate : 'live'
	));

	useEffect(() => {
		if (initialMode === 'playback' && initialDate) {
			setSelectedDate(initialDate);
			return;
		}
		if (initialMode === 'live') setSelectedDate('live');
	}, [initialMode, initialDate]);

	// 录像段 + 加载状态（Sprint 7.2）
	const [segments, setSegments] = useState<CloudSegment[] | null>(null);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [historyError, setHistoryError] = useState<string | null>(null);
	const [playToken, setPlayToken] = useState<PlayTokenData | null>(null);
	const [playTokenLoading, setPlayTokenLoading] = useState(false);

	const liveTokenDeviceId = useMemo(() => tokenDeviceId(playToken, 'live'), [playToken]);
	const playbackTokenDeviceId = useMemo(() => tokenDeviceId(playToken, 'playback'), [playToken]);
	const playbackUrlDeviceId = playDeviceIdParam || liveTokenDeviceId || deviceSn;
	const historyDeviceCandidates = useMemo(
		() => uniqueText([historyDeviceIdParam, deviceSn, liveTokenDeviceId, playbackTokenDeviceId]),
		[historyDeviceIdParam, deviceSn, liveTokenDeviceId, playbackTokenDeviceId],
	);
	const historyDeviceKey = historyDeviceCandidates.join('|');

	// 当前选中设备的 device_id（用 SN 查 storeDevices 拿）— PTZ 接口要 device_auto_id 不是 SN
	const currentDevice = useMemo(
		() => (storeDevices ?? []).find((d) => String(d.sn ?? '') === deviceSn),
		[storeDevices, deviceSn],
	);

	// 设备能力（device/device_model_list）— 决定 PTZ/对讲按钮是否可点
	const [caps, setCaps] = useState<DeviceCapabilities | null>(null);
	useEffect(() => {
		if (!currentDevice?.device_id) { setCaps(null); return; }
		void fetchDeviceCapabilities(currentDevice.device_id, channel).then(setCaps);
	}, [currentDevice?.device_id, channel]);

	useEffect(() => {
		if (!recording || !recordStartedAt) {
			setRecordElapsedMs(0);
			return;
		}
		const tick = () => setRecordElapsedMs(Date.now() - recordStartedAt);
		tick();
		const id = setInterval(tick, 1000);
		return () => clearInterval(id);
	}, [recording, recordStartedAt]);

	useEffect(() => {
		if (!deviceSn || !storeId || selectedDate !== 'live') return;
		const key = `${storeId}-${deviceSn}-${channel}`;
		if (recentSeedKeyRef.current === key) return;
		recentSeedKeyRef.current = key;
		pushRecentRecord({
			storeId,
			storeName,
			deviceSn,
			deviceLabel,
			channel,
			viewedAt: Date.now(),
			online: true,
		});
	}, [storeId, storeName, deviceSn, deviceLabel, channel, selectedDate]);

	// 播放成功后 1.5s 截当前帧 + 写入"最近观看"localStorage
	useEffect(() => {
		if (status !== 'playing' || !deviceSn || !storeId) return;
		const tid = setTimeout(() => {
			const v = document.querySelector('video') as HTMLVideoElement | null;
			pushRecentRecord({
				storeId, storeName, deviceSn, deviceLabel, channel,
				viewedAt: Date.now(),
				thumbDataUrl: captureVideoThumb(v),
				online: true,
			});
		}, 1500);
		return () => clearTimeout(tid);
	}, [status, storeId, storeName, deviceSn, deviceLabel, channel]);

	// 加载实时画面（仅 live 模式）
	useEffect(() => {
		if (!deviceSn || selectedDate !== 'live') return;
		void playerRef.current?.loadDevice(deviceSn, channel, deviceLabel);
	}, [deviceSn, channel, deviceLabel, selectedDate]);

	useEffect(() => {
		if (!deviceAutoId) {
			setPlayToken(null);
			setPlayTokenLoading(false);
			return;
		}
		let cancelled = false;
		setPlayTokenLoading(true);
		fetchPlayToken({ deviceAutoId, channelId: channel })
			.then((token) => {
				if (!cancelled) setPlayToken(token);
			})
			.catch(() => {
				if (!cancelled) setPlayToken(null);
			})
			.finally(() => {
				if (!cancelled) setPlayTokenLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [deviceAutoId, channel]);

	// 切日期拉录像段（Sprint 7.2 — m3u8api/video/history）
	useEffect(() => {
		if (historyDeviceCandidates.length === 0) return;
		if (selectedDate === 'live' || selectedDate === 'all') {
			setSegments(null);
			setHistoryError(null);
			return;
		}
		const day = dayjs(selectedDate);
		if (!day.isValid()) return;
		let cancelled = false;
		setHistoryLoading(true);
		setHistoryError(null);
		const startTime = day.startOf('day').unix();
		const endTime = day.endOf('day').unix();
		(async () => {
			let lastError = '';
			let lastData: CloudSegment[] = [];
			for (const candidate of historyDeviceCandidates) {
				try {
					const res = await fetchCloudHistory({
						device_id: candidate,
						channel_id: channel,
						start_time: startTime,
						end_time: endTime,
					});
					if (Number(res.code) !== 0) {
						lastError = `${res.msg || 'cloud history failed'} (code=${res.code})`;
						continue;
					}
					const data = res.data ?? [];
					lastData = data;
					if (data.length > 0) {
						if (!cancelled) {
							setSegments(data);
							setHistoryError(null);
						}
						return;
					}
				} catch (e) {
					lastError = (e as Error).message;
				}
			}
			if (!cancelled) {
				setSegments(lastData);
				setHistoryError(lastError || null);
			}
		})()
			.finally(() => {
				if (!cancelled) setHistoryLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [historyDeviceCandidates, historyDeviceKey, channel, selectedDate]);

	useEffect(() => {
		if (initialMode !== 'playback' || !initialAt || !initialDate) return;
		if (!playbackUrlDeviceId || selectedDate !== initialDate || historyLoading || !segments) return;
		if (deviceAutoId && playTokenLoading) return;
		const atUtc = parsePlaybackAt(initialAt);
		if (!Number.isFinite(atUtc)) return;
		const key = `${playbackUrlDeviceId}-${historyDeviceKey}-${channel}-${selectedDate}-${initialAt}-${segments.length}`;
		if (autoPlaybackKeyRef.current === key) return;
		autoPlaybackKeyRef.current = key;
		const segment = findPlaybackSegment(segments, atUtc);
		if (!segment) {
			message.info(t('player.no_record_at_time'));
			return;
		}
		const safeEnd = Math.max(segment.start_utc, segment.end_utc - 1);
		const startTime = Math.min(Math.max(atUtc, segment.start_utc), safeEnd);
		void fetchPlaybackUrl({
			device_id: playbackUrlDeviceId,
			channel_id: channel,
			start_time: startTime,
			end_time: segment.end_utc,
		}).then((url) => {
			playerRef.current?.loadUrl(url, 'hls', `${deviceLabel} ${dayjs.unix(startTime).format('HH:mm:ss')}`);
			message.success(t('player.playback_started'));
		}).catch((e) => {
			message.error((e as Error).message);
		});
	}, [channel, deviceAutoId, deviceLabel, historyDeviceKey, historyLoading, initialAt, initialDate, initialMode, message, playbackUrlDeviceId, playTokenLoading, segments, selectedDate, t]);

	// 拉同店设备列表（用于切换 dropdown）
	useEffect(() => {
		if (!storeId) return;
		callInternal<DeviceLite[]>({
			short_name: 'base',
			action: '/device/store_device_list_with_status',
			query: { store_ids: storeId },
			av: '1.1',
		})
			.then((res) => {
				if (res.code === 0 && Array.isArray(res.data)) setStoreDevices(res.data);
				else setStoreDevices([]);
			})
			.catch(() => setStoreDevices([]));
	}, [storeId]);

	useEffect(() => {
		if (!storeId) {
			setStoreContact(null);
			setStoreContactError(null);
			return;
		}
		setStoreContactError(null);
		callInternal<StoreContact[]>({
			short_name: 'base',
			action: '/store/visible_stores',
			method: 'GET',
		})
			.then((res) => {
				if (res.code === 0 && Array.isArray(res.data)) {
					setStoreContact(res.data.find((s) => String(s.store_id) === String(storeId)) ?? null);
				} else {
					setStoreContact(null);
					setStoreContactError(region === 'apac' ? null : `${res.msg || 'visible_stores failed'} (code=${res.code})`);
				}
			})
			.catch((e) => {
				setStoreContact(null);
				setStoreContactError(region === 'apac' ? null : (e instanceof Error ? e.message : String(e)));
			});
	}, [region, storeId]);

	const phoneNumbers = useMemo(
		() => collectStorePhones(storeContact),
		[storeContact],
	);

	const switchDevice = (d: DeviceLite) => {
		const next = new URLSearchParams(params);
		next.set('deviceSn', d.sn ?? '');
		next.set('deviceLabel', formatDeviceLabel(d));
		next.set('channel', '1');
		setParams(next, { replace: true });
		setSwitcherOpen(false);
	};

	const togglePause = () => {
		const v = document.querySelector('video') as HTMLVideoElement | null;
		if (!v) return;
		if (v.paused) { void v.play(); setPaused(false); }
		else { v.pause(); setPaused(true); }
	};
	const toggleMute = () => {
		const v = document.querySelector('video') as HTMLVideoElement | null;
		if (!v) return;
		v.muted = !v.muted;
		setMuted(v.muted);
	};
	const cycleSpeed = () => {
		const seq: (0.5 | 1 | 2 | 4)[] = [1, 2, 4, 0.5];
		const idx = seq.indexOf(speed);
		const next = seq[(idx + 1) % seq.length];
		setSpeed(next);
		const v = document.querySelector('video') as HTMLVideoElement | null;
		if (v) v.playbackRate = next;
	};
	const cycleRotation = () => setRotation((r) => (r + 90) % 360);

	const toggleFullscreen = async () => {
		const el = fsContainerRef.current;
		if (!el) return;
		try {
			if (document.fullscreenElement) {
				await document.exitFullscreen();
				// unlockOrientation 由 fullscreenchange 监听统一处理
			} else {
				await el.requestFullscreen();
				await lockLandscape();
			}
		} catch (e) {
			message.warning((e as Error).message);
		}
	};

	// 监听系统手势退出全屏（ESC / Android 返回键 / iOS 系统手势）→ 同步 isFs + 解锁横屏
	useEffect(() => {
		const onChange = () => {
			const fs = !!document.fullscreenElement;
			setIsFs(fs);
			if (!fs) void unlockOrientation();
		};
		document.addEventListener('fullscreenchange', onChange);
		return () => document.removeEventListener('fullscreenchange', onChange);
	}, []);

	// 6 功能按钮
	const onScreenshot = () => {
		const v = document.querySelector('video') as HTMLVideoElement | null;
		if (!v || !v.videoWidth) return message.warning(t('player.no_stream'));
		const c = document.createElement('canvas');
		c.width = v.videoWidth; c.height = v.videoHeight;
		c.getContext('2d')?.drawImage(v, 0, 0);
		const dataUrl = c.toDataURL('image/png');
		const filename = `${deviceLabel}-${dayjs().format('YYYYMMDD-HHmmss')}.png`;
		void exportDataUrlForUser(dataUrl, filename, {
			title: filename,
			text: `${storeName || 'Ulucu'} ${deviceLabel} channel ${channel}`,
		})
			.then((mode) => {
				if (mode === 'canceled') return;
				if (mode === 'native') message.success('截图已生成，请在系统面板保存或分享');
				else if (mode === 'download') message.success(t('player.screenshot_done'));
				else message.warning('当前手机端暂不支持截图导出，请在浏览器端下载');
			})
			.catch((e) => message.error((e as Error).message));
		// 2) 同时写入「我的相册」localStorage（jpeg 50% 缩小存储压力）
		try {
			const c2 = document.createElement('canvas');
			const W = Math.min(640, v.videoWidth);
			c2.width = W; c2.height = Math.round(W * v.videoHeight / v.videoWidth);
			c2.getContext('2d')?.drawImage(v, 0, 0, c2.width, c2.height);
			const small = c2.toDataURL('image/jpeg', 0.7);
			const KEY = 'ulucu.album';
			const list = (() => { try { return JSON.parse(localStorage.getItem(KEY) ?? '[]') as Array<{ url: string; ts: number; label?: string }>; } catch { return []; } })();
			list.unshift({ url: small, ts: Date.now(), label: deviceLabel });
			localStorage.setItem(KEY, JSON.stringify(list.slice(0, 60)));
		} catch { /* quota / DOM error 都忽略，不影响下载 */ }
	};

	// 录像：MediaRecorder 录制 video 元素的 captureStream，停时下载 webm
	const resetRecordingState = () => {
		setRecording(false);
		setRecordingMode(null);
		setRecordStartedAt(null);
		setRecordElapsedMs(0);
	};

	const startNativeRecording = async () => {
		const recorder = getNativeScreenRecorder();
		if (!recorder) {
			message.warning('手机端录像插件未注册，请安装最新 APK');
			return;
		}
		try {
			const available = await recorder.isAvailable();
			if (!available.available) {
				message.warning(available.reason || '当前手机端不支持原生录像');
				return;
			}
			setRecordingMode('native');
			setRecording(true);
			setRecordStartedAt(Date.now());
			const filename = `${safeFilePart(deviceLabel || deviceSn || 'ulucu')}-${dayjs().format('YYYYMMDD-HHmmss')}.mp4`;
			const result = await recorder.start({
				filename,
				title: `${storeName || 'Ulucu'} ${deviceLabel || deviceSn}`,
			});
			setRecordStartedAt(result.startedAt ?? Date.now());
			message.success('录像已开始，轻点视频画面可停止录像');
		} catch (e) {
			resetRecordingState();
			message.error(readErrorMessage(e, '原生录像启动失败'));
		}
	};

	const stopNativeRecording = async () => {
		const recorder = getNativeScreenRecorder();
		if (!recorder) {
			resetRecordingState();
			message.warning('手机端录像插件未注册，请安装最新 APK');
			return;
		}
		try {
			const result = await recorder.stop();
			resetRecordingState();
			if (result.uri) {
				const shared = await shareWithNativeSheet({
					title: result.filename ?? 'Ulucu recording',
					text: `${storeName || 'Ulucu'} ${deviceLabel || deviceSn}`,
					files: [result.uri],
					dialogTitle: '保存或分享录像',
				});
				if (shared) {
					message.success('录像已生成，请在系统面板保存或分享');
				} else {
					message.success(`录像已保存：${result.filename ?? result.uri}`);
				}
			} else {
				message.success('录像已保存');
			}
		} catch (e) {
			resetRecordingState();
			message.error(readErrorMessage(e, '原生录像停止失败'));
		}
	};

	const toggleRecord = () => {
		const v = document.querySelector('video') as HTMLVideoElement | null;
		if (recording) {
			if (recordingMode === 'native') {
				void stopNativeRecording();
				return;
			}
			if (recorderRef.current) recorderRef.current.stop();
			return;
		}
		if (isCapacitor() && region === 'apac') {
			if (!hasDevice || status === 'error') {
				message.warning(t('player.no_stream'));
				return;
			}
			void startNativeRecording();
			return;
		}
		if (!v || !v.videoWidth) {
			message.warning(t('player.no_stream'));
			return;
		}
		if (isCapacitor()) {
			message.warning('手机端录像暂未接入原生导出，请在浏览器端使用');
			return;
		}
		if (typeof MediaRecorder === 'undefined') {
			message.warning('当前浏览器不支持录像');
			return;
		}
		try {
			const stream = (v as HTMLVideoElement & { captureStream?: () => MediaStream }).captureStream?.();
			if (!stream) {
				message.warning('captureStream not supported');
				return;
			}
			const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm';
			const rec = new MediaRecorder(stream, { mimeType: mime });
			recordChunksRef.current = [];
			rec.ondataavailable = (e) => {
				if (e.data && e.data.size > 0) recordChunksRef.current.push(e.data);
			};
			rec.onstop = () => {
				const blob = new Blob(recordChunksRef.current, { type: mime });
				const filename = `${deviceLabel}-${dayjs().format('YYYYMMDD-HHmmss')}.webm`;
				void exportBlobForUser(blob, filename, {
					title: filename,
					text: `${storeName || 'Ulucu'} ${deviceLabel} channel ${channel}`,
				})
					.then((mode) => {
						if (mode === 'canceled') return;
						if (mode === 'native') {
							message.success('录像已生成，请在系统面板保存或分享');
						} else if (mode === 'download') {
							message.success(t('player.recording_saved'));
						} else {
							message.warning('当前手机端暂不支持录像导出，请在浏览器端下载');
						}
					})
					.catch((e) => {
						message.error((e as Error).message);
					})
					.finally(() => {
						resetRecordingState();
						recorderRef.current = null;
					});
			};
			rec.start(1000);
			recorderRef.current = rec;
			setRecording(true);
			setRecordingMode('web');
			setRecordStartedAt(Date.now());
			message.info(t('player.recording_started'));
		} catch (e) {
			message.error((e as Error).message);
		}
	};

	const onIntercom = () => {
		if (isApac) return;
		message.info(`${t('player.fn_intercom')} - ${t('common.todo_module')}`);
	};
	const dialPhone = (phone: string) => {
		if (typeof window !== 'undefined') window.location.href = `tel:${phone.replace(/[^\d+]/g, '')}`;
	};
	const onPhone = () => {
		if (phoneNumbers.length === 0) {
			const suffix = !isApac && storeContactError ? `：${storeContactError}` : '';
			message.warning(`${t('store_detail.no_phone')}${suffix}`);
			return;
		}
		if (phoneNumbers.length === 1) {
			dialPhone(phoneNumbers[0]);
			return;
		}
		setPhoneOpen(true);
	};
	const onShare = async () => {
		if (typeof window === 'undefined') return;
		const title = [storeName, deviceLabel].filter(Boolean).join(' · ') || 'Ulucu video';
		const lines = [
			title,
			storeId ? `Store ID: ${storeId}` : '',
			deviceSn ? `Device SN: ${deviceSn}` : '',
			`Channel: ${channel}`,
			window.location.href,
		].filter(Boolean);
		const text = lines.join('\n');
		const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
		if (nav.share && !isCapacitor()) {
			try {
				await nav.share({ title, text, url: window.location.href });
				return;
			} catch (e) {
				if ((e as Error).name === 'AbortError') return;
			}
		}
		if (await shareWithNativeSheet({ title, text, url: window.location.href })) return;
		const copied = await copyText(text);
		if (copied) {
			message.success(t('store_detail.copied'));
		} else {
			setShareFallbackText(text);
			message.warning('系统分享/复制不可用，请长按下方文本复制');
		}
	};
	const moduleActionState = (moduleKey: string): CapabilityState => {
		const cap = capabilityForModule(moduleKey);
		return cap ? capabilityState(cap, region) : 'available';
	};
	const capabilityMessage = (state: CapabilityState) => {
		if (state === 'permission_required') return t('capability.permission_required_short');
		if (state === 'unknown') return t('capability.unknown_short');
		if (state === 'region_unavailable') return t('capability.region_unavailable_short');
		return '';
	};
	const captureFrameForEvent = () => {
		const v = document.querySelector('video') as HTMLVideoElement | null;
		if (!v || !v.videoWidth || !v.videoHeight) return '';
		try {
			const c = document.createElement('canvas');
			c.width = v.videoWidth;
			c.height = v.videoHeight;
			const ctx = c.getContext('2d');
			if (!ctx) return '';
			ctx.drawImage(v, 0, 0);
			if (isCanvasMostlyBlank(c)) return '';
			return c.toDataURL('image/jpeg', 0.82);
		} catch {
			return '';
		}
	};
	const openCreateEventFromPlayer = () => {
		const state = moduleActionState('event');
		if (state !== 'available') {
			message.warning(capabilityMessage(state));
			return;
		}
		if (!storeId || !deviceSn || !channel) {
			message.warning(t('native.inspect.device_context_missing'));
			return;
		}
		const frame = captureFrameForEvent();
		navigate('/m/home/events/create', {
			state: {
				source: 'player',
				storeId,
				storeName,
				deviceName: deviceLabel || deviceSn,
				deviceSn,
				channelId: String(channel),
				captureTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
				imageDataUrl: frame || undefined,
				returnTo: '/m/home',
			},
		});
	};
	const openCapabilityRoute = (moduleKey: string, route: string) => {
		const state = moduleActionState(moduleKey);
		if (state !== 'available') {
			message.warning(capabilityMessage(state));
			return;
		}
		navigate(route);
	};
	const eventState = moduleActionState('event');
	const posState = moduleActionState('pos');
	const recordButtonLabel = recording
		? `${t('player.fn_record_stop')} ${formatRecordDuration(recordElapsedMs)}`
		: t('player.fn_record');

	// header 下拉箭头方向（switcherOpen 时翻转）
	const ArrowIcon = switcherOpen ? CaretDownOutlined : CaretDownOutlined;

	return (
		<div style={{ background: nativeCleanRecording ? '#000' : PAGE_BG, minHeight: '100vh', paddingBottom: nativeCleanRecording ? 0 : 24 }}>
		<div style={{ maxWidth: nativeCleanRecording ? 'none' : 880, margin: '0 auto' }}>
			{/* ===== 顶部 header ===== */}
			{!nativeCleanRecording && (
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					padding: '12px 12px 8px',
				}}
			>
				<Button
					type="text"
					shape="circle"
					icon={<LeftOutlined />}
					onClick={() => navigate(-1)}
					style={{ background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', width: 36, height: 36 }}
				/>
				<div
					onClick={() => { if (storeId) setSwitcherOpen(true); }}
					style={{
						background: '#fff',
						borderRadius: 22,
						padding: '6px 14px',
						boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						minWidth: 140,
						cursor: storeId ? 'pointer' : 'default',
					}}
				>
					<Typography.Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
						{storeName}
					</Typography.Text>
					<Typography.Text strong style={{ fontSize: 14, lineHeight: 1.4 }}>
						{deviceLabel} <ArrowIcon style={{ fontSize: 11, color: PRIMARY, marginInlineStart: 2 }} />
					</Typography.Text>
				</div>
				<div style={{ display: 'flex', gap: 6 }}>
					<Button
						type="text"
						shape="circle"
						aria-label={t('store_detail.call_owner')}
						icon={<PhoneOutlined style={{ color: PRIMARY }} />}
						onClick={onPhone}
						style={{ background: '#fff', width: 36, height: 36 }}
					/>
					<Button
						type="text"
						shape="circle"
						aria-label={t('player.share')}
						icon={<ShareAltOutlined style={{ color: PRIMARY }} />}
						onClick={() => void onShare()}
						style={{ background: '#fff', width: 36, height: 36 }}
					/>
				</div>
			</div>
			)}

			{/* ===== 视频区 ===== */}
			<div
				ref={fsContainerRef}
				onClick={nativeCleanRecording ? () => void stopNativeRecording() : undefined}
				style={{
					position: 'relative',
					background: '#000',
					overflow: 'hidden',
					...(isFs
						? { width: '100%', height: '100%' }
						: nativeCleanRecording
							? { width: '100%', height: '100vh' }
							: { aspectRatio: '16/9' }),
				}}
			>
				{/* 全屏退出按钮：仅全屏态显示 */}
				{isFs && !nativeCleanRecording && (
					<Button
						type="text"
						icon={<FullscreenExitOutlined style={{ color: '#fff', fontSize: 22 }} />}
						onClick={toggleFullscreen}
						style={{
							position: 'absolute',
							insetInlineStart: 12,
							top: 'calc(12px + env(safe-area-inset-top))',
							width: 40,
							height: 40,
							background: 'rgba(0,0,0,0.5)',
							borderRadius: '50%',
							zIndex: 100,
						}}
					/>
				)}
				{hasDevice ? (
					<div
						style={{
							width: '100%',
							height: '100%',
							transform: rotation ? `rotate(${rotation}deg)` : undefined,
							transformOrigin: 'center',
						}}
					>
						<UluVideoPlayer
							ref={playerRef}
							bareMode
							onStatusChange={(s, e) => {
								setStatus(s);
								setErrMsg(e ?? null);
							}}
						/>
					</div>
				) : (
					<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#fff', opacity: 0.7 }}>
						<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ color: '#aaa' }}>{t('player.no_device')}</span>} />
					</div>
				)}

				{/* 状态/速率水印 */}
				{hasDevice && !nativeCleanRecording && status === 'playing' && (
					<div
						style={{
							position: 'absolute',
							top: 8,
							right: 12,
							display: 'flex',
							alignItems: 'center',
							gap: 4,
							color: '#fff',
							fontSize: 12,
							background: 'rgba(0,0,0,0.35)',
							padding: '2px 8px',
							borderRadius: 12,
						}}
					>
						<span style={{ color: PRIMARY, fontSize: 13 }}>⚡</span>
						<span>—</span>
					</div>
				)}

				{hasDevice && !nativeCleanRecording && status === 'loading' && (
					<div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<Spin />
					</div>
				)}
				{hasDevice && !nativeCleanRecording && status === 'error' && errMsg && (
					<div
						style={{
							position: 'absolute',
							bottom: 12,
							left: 12,
							right: 12,
							background: 'rgba(255,77,79,0.9)',
							color: '#fff',
							padding: '6px 10px',
							borderRadius: 6,
							fontSize: 12,
						}}
					>
						{errMsg}
					</div>
				)}
			</div>

			{hasDevice ? (
			nativeCleanRecording ? null : (
			<>
			{/* ===== 视频控制栏 ===== */}
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-around',
					padding: '8px 4px',
					background: PAGE_BG,
				}}
			>
				<Button type="text" icon={paused ? <PlayCircleFilled style={{ fontSize: 22, color: '#666' }} /> : <PauseCircleFilled style={{ fontSize: 22, color: '#666' }} />} onClick={togglePause} />
				<Button type="text" icon={muted ? <AudioMutedOutlined style={{ fontSize: 22 }} /> : <SoundOutlined style={{ fontSize: 22 }} />} onClick={toggleMute} />
				<button
					onClick={() => setQuality((q) => (q === 'hd' ? 'sd' : 'hd'))}
					style={btnText(quality === 'hd' ? PRIMARY : '#666')}
				>
					{t(quality === 'hd' ? 'player.hd' : 'player.sd')}
				</button>
				<button onClick={cycleRotation} style={btnText('#666')}>R {rotation}deg</button>
				<button onClick={cycleSpeed} style={btnText(speed !== 1 ? PRIMARY : '#666')}>{speed}x</button>
				<button onClick={() => setFilled((f) => !f)} style={btnText(filled ? PRIMARY : '#666')}>
					{t(filled ? 'player.fit' : 'player.fill')}
				</button>
				<Button type="text" icon={<FullscreenOutlined style={{ fontSize: 20 }} />} onClick={toggleFullscreen} />
			</div>

			{/* ===== 日期切换条：日历图标 + 可横滚日期 chips + 固定的实时/全部 ===== */}
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: 6,
					padding: '8px 12px',
					background: '#fff',
					borderTop: '1px solid #f0f0f0',
				}}
			>
				<CalendarOutlined style={{ color: PRIMARY, fontSize: 18, marginInlineEnd: 4, flexShrink: 0 }} />
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 6,
						flex: 1,
						minWidth: 0,
						overflowX: 'auto',
						whiteSpace: 'nowrap',
					}}
				>
					{dateChips.map((c) => (
						<DateChipBtn
							key={c.key}
							active={selectedDate === c.key}
							onClick={() => setSelectedDate(c.key)}
							label={c.label}
						/>
					))}
				</div>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 6,
						flexShrink: 0,
						marginInlineStart: 6,
						paddingInlineStart: 6,
						borderInlineStart: '1px solid #f0f0f0',
					}}
				>
					<DateChipBtn active={selectedDate === 'live'} onClick={() => setSelectedDate('live')} label={t('player.live')} outlined />
					<DateChipBtn active={selectedDate === 'all'}  onClick={() => setSelectedDate('all')}  label={t('player.all')} outlined />
				</div>
			</div>

			{/* ===== 录像时间轴（24h/1h/10min 三档缩放 + 拖拽 + pinch；接 m3u8api 真区段） ===== */}
			<VideoTimeline
				selectedDate={selectedDate}
				segments={segments}
				loading={historyLoading}
				error={historyError}
				emptyText={t('player.no_history')}
				loadingText={t('common.loading', { defaultValue: 'Loading...' })}
				segmentCountText={(count) => t('player.history_segment_count', { count, defaultValue: `${count} segments` })}
				onSeek={async (utc, seg) => {
					if (!seg) {
						message.info(t('player.no_record_at_time'));
						return;
					}
					if (!playbackUrlDeviceId) return;
					try {
						const url = await fetchPlaybackUrl({
							device_id: playbackUrlDeviceId,
							channel_id: channel,
							start_time: utc,
							end_time: seg.end_utc,
						});
						playerRef.current?.loadUrl(url, 'hls', `${deviceLabel} · ${dayjs.unix(utc).format('HH:mm:ss')}`);
						message.success(t('player.playback_started'));
					} catch (e) {
						message.error((e as Error).message);
					}
				}}
			/>

			{/* ===== 查看图例 ===== */}
			<div style={{ textAlign: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0', background: '#fff' }}>
				<Typography.Text type="secondary" style={{ fontSize: 12 }}>
					{t('player.view_legend')} <CaretDownOutlined style={{ fontSize: 11 }} />
				</Typography.Text>
			</div>

			{/* ===== 6 功能按钮 ===== */}
			<div style={{ padding: 16 }}>
				<div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', rowGap: 20, columnGap: 16 }}>
					<FnButton Icon={CameraOutlined}     label={t('player.fn_screenshot')}    onClick={onScreenshot} />
					<FnButton
						Icon={AudioOutlined}
						label={t('player.fn_intercom')}
						disabled={caps != null && !caps.supports(FN.AUDIO_OUT)}
						disabledMessage={t('player.cap_unsupported')}
						onDisabled={(text) => { if (!isApac) message.warning(text); }}
						onClick={onIntercom}
					/>
					<FnButton Icon={VideoCameraOutlined} label={recordButtonLabel} onClick={toggleRecord} active={recording} />
					<FnButton
						Icon={EditOutlined}
						label={t('player.fn_event')}
						disabled={eventState !== 'available'}
						disabledMessage={capabilityMessage(eventState)}
						onDisabled={(text) => { if (!isApac) message.warning(text); }}
						onClick={openCreateEventFromPlayer}
					/>
					<FnButton
						Icon={CompassOutlined}
						label={t('player.fn_ptz')}
						disabled={caps != null && !caps.supports(FN.PTZ)}
						disabledMessage={t('player.cap_unsupported')}
						onClick={() => setPtzOpen(true)}
					/>
					<FnButton
						Icon={FileSearchOutlined}
						label={t('player.fn_pos')}
						disabled={posState !== 'available'}
						disabledMessage={capabilityMessage(posState)}
						onDisabled={(text) => { if (!isApac) message.warning(text); }}
						onClick={() => openCapabilityRoute('pos', '/pos')}
					/>
					</div>
				</div>
			</>
			)
			) : (
				<NoDevicePanel
					canSwitch={Boolean(storeId)}
					onSwitch={() => setSwitcherOpen(true)}
				/>
			)}

			{/* ===== 设备切换 Drawer ===== */}
			<Drawer
				open={switcherOpen}
				onClose={() => setSwitcherOpen(false)}
				placement="top"
				height="55%"
				title={
					<div style={{ textAlign: 'center' }}>
						<Typography.Text type="secondary" style={{ fontSize: 11 }}>{storeName}</Typography.Text>
						<div>
							<Typography.Text strong>{deviceLabel}</Typography.Text>
						</div>
					</div>
				}
			>
				{!storeDevices && <Spin />}
				{storeDevices && storeDevices.length === 0 && <Empty />}
				{storeDevices?.map((d) => {
					const isAudio = String(d.type_id) === '7';
					const isCurrent = String(d.sn ?? d.device_id) === deviceSn;
					return (
						<div
							key={String(d.device_id)}
							onClick={() => switchDevice(d)}
							style={{
								padding: '14px 12px',
								borderBottom: '1px solid #f0f0f0',
								display: 'flex',
								alignItems: 'center',
								gap: 12,
								background: isCurrent ? '#FFEFE3' : 'transparent',
								cursor: 'pointer',
								opacity: String(d.status) === '1' ? 1 : 0.6,
							}}
						>
							{isAudio ? (
								<AudioOutlined style={{ fontSize: 18, color: isCurrent ? PRIMARY : '#666' }} />
							) : (
								<VideoCameraOutlined style={{ fontSize: 18, color: isCurrent ? PRIMARY : '#666' }} />
							)}
							<span style={{ flex: 1, color: isCurrent ? PRIMARY : '#333' }}>
								{formatDeviceLabel(d)}
							</span>
							{isCurrent && <CheckCircleFilled style={{ color: PRIMARY }} />}
						</div>
					);
				})}
			</Drawer>

			<Drawer
				open={phoneOpen}
				onClose={() => setPhoneOpen(false)}
				placement="bottom"
				height={Math.min(320, 120 + phoneNumbers.length * 56)}
				title={t('store_detail.call_owner')}
			>
				{phoneNumbers.length === 0 && <Empty description={t('store_detail.no_phone')} />}
				{phoneNumbers.map((phone) => (
					<Button
						key={phone}
						block
						type="text"
						icon={<PhoneOutlined style={{ color: PRIMARY }} />}
						onClick={() => dialPhone(phone)}
						style={{ height: 48, justifyContent: 'flex-start', marginBottom: 8 }}
					>
						{phone}
					</Button>
				))}
			</Drawer>

			<Drawer
				open={Boolean(shareFallbackText)}
				onClose={() => setShareFallbackText(null)}
				placement="bottom"
				height={320}
				title="分享信息"
			>
				<Typography.Text type="secondary">
					系统分享和剪贴板当前不可用，请长按下方内容手动复制。
				</Typography.Text>
				<textarea
					readOnly
					value={shareFallbackText ?? ''}
					onFocus={(e) => e.currentTarget.select()}
					style={{
						boxSizing: 'border-box',
						width: '100%',
						minHeight: 180,
						marginTop: 12,
						padding: 12,
						borderRadius: 8,
						border: '1px solid #d9d9d9',
						fontSize: 13,
						lineHeight: 1.5,
						resize: 'none',
						userSelect: 'text',
					}}
				/>
			</Drawer>

			{/* ===== PTZ 云台 Drawer ===== */}
			<Drawer
				open={ptzOpen}
				onClose={() => setPtzOpen(false)}
				placement="bottom"
				height={360}
				title={t('player.fn_ptz')}
			>
				<div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
					{currentDevice ? (
						<PtzControls
							deviceAutoId={currentDevice.device_id}
							channelId={channel}
							speed={3}
						/>
					) : (
						<Empty description={t('player.no_device')} />
					)}
				</div>
			</Drawer>
		</div>
		</div>
	);
}

function btnText(color: string): React.CSSProperties {
	return { background: 'transparent', border: 'none', color, fontSize: 13, padding: '0 8px', cursor: 'pointer' };
}

function collectStorePhones(store: StoreContact | null): string[] {
	const more = Array.isArray(store?.more_phone)
		? store?.more_phone
		: typeof store?.more_phone === 'string'
			? store.more_phone.split(/[,，;\s]+/)
			: [];
	const phones = [store?.store_phone, store?.shopowner_phone, ...(more ?? [])]
		.map((phone) => String(phone ?? '').trim())
		.filter((phone) => phone.replace(/[^\d]/g, '').length >= 7);
	return Array.from(new Set(phones));
}

function getCapacitorPlugins(): CapacitorGlobal['Plugins'] | undefined {
	if (typeof window === 'undefined' || !isCapacitor()) return undefined;
	return (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor?.Plugins;
}

function getNativeScreenRecorder(): CapacitorScreenRecorderPlugin | undefined {
	return getCapacitorPlugins()?.ScreenRecorder;
}

function safeFilePart(value: string): string {
	const safe = value
		.trim()
		.replace(/\s+/g, '-')
		.replace(/[^a-zA-Z0-9._-]/g, '-')
		.replace(/-+/g, '-')
		.replace(/^-|-$/g, '');
	return safe || 'ulucu';
}

function formatRecordDuration(ms: number): string {
	const total = Math.max(0, Math.floor(ms / 1000));
	const m = Math.floor(total / 60).toString().padStart(2, '0');
	const s = (total % 60).toString().padStart(2, '0');
	return `${m}:${s}`;
}

function readErrorMessage(e: unknown, fallback: string): string {
	if (e instanceof Error && e.message) return e.message;
	const value = String(e ?? '').trim();
	return value || fallback;
}

async function shareWithNativeSheet(options: {
	title?: string;
	text?: string;
	url?: string;
	files?: string[];
	dialogTitle?: string;
}): Promise<boolean> {
	const share = getCapacitorPlugins()?.Share;
	if (!share) return false;
	try {
		await share.share(options);
		return true;
	} catch (e) {
		if ((e as Error).message?.toLowerCase().includes('cancel')) return true;
		return false;
	}
}

type ExportMode = 'download' | 'native' | 'unavailable' | 'canceled';

async function exportDataUrlForUser(
	dataUrl: string,
	filename: string,
	shareOptions: { title?: string; text?: string },
): Promise<ExportMode> {
	const base64 = dataUrl.split(',')[1];
	if (!base64) return 'unavailable';
	if (isCapacitor()) {
		return exportBase64WithNativeShare(base64, filename, shareOptions);
	}
	downloadHref(dataUrl, filename);
	return 'download';
}

async function exportBlobForUser(
	blob: Blob,
	filename: string,
	shareOptions: { title?: string; text?: string },
): Promise<ExportMode> {
	if (isCapacitor()) {
		return exportBase64WithNativeShare(await blobToBase64(blob), filename, shareOptions);
	}
	const url = URL.createObjectURL(blob);
	downloadHref(url, filename);
	setTimeout(() => URL.revokeObjectURL(url), 1000);
	return 'download';
}

async function exportBase64WithNativeShare(
	base64Data: string,
	filename: string,
	shareOptions: { title?: string; text?: string },
): Promise<ExportMode> {
	const plugins = getCapacitorPlugins();
	if (!plugins?.Filesystem || !plugins.Share) return 'unavailable';
	const path = `exports/${filename}`;
	const result = await plugins.Filesystem.writeFile({
		path,
		data: base64Data,
		directory: 'CACHE',
		recursive: true,
	});
	try {
		await plugins.Share.share({
			title: shareOptions.title ?? filename,
			text: shareOptions.text,
			files: [result.uri],
			dialogTitle: '保存或分享',
		});
	} catch (e) {
		if (isShareCanceled(e)) return 'canceled';
		throw e;
	}
	return 'native';
}
function isShareCanceled(e: unknown): boolean {
	const message = e instanceof Error ? e.message : String(e ?? '');
	return /cancel(?:ed|led)?/i.test(message);
}

function downloadHref(href: string, filename: string): void {
	const a = document.createElement('a');
	a.download = filename;
	a.href = href;
	a.click();
}

function blobToBase64(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const value = String(reader.result ?? '');
			resolve(value.includes(',') ? value.split(',')[1] : value);
		};
		reader.onerror = () => reject(reader.error ?? new Error('Failed to read recording'));
		reader.readAsDataURL(blob);
	});
}

async function copyText(text: string): Promise<boolean> {
	if (navigator.clipboard?.writeText) {
		try {
			await navigator.clipboard.writeText(text);
			return true;
		} catch {
			// Fall through to the textarea fallback for older WebViews.
		}
	}
	try {
		const textarea = document.createElement('textarea');
		textarea.value = text;
		textarea.setAttribute('readonly', 'true');
		textarea.style.position = 'fixed';
		textarea.style.top = '0';
		textarea.style.left = '0';
		textarea.style.width = '1px';
		textarea.style.height = '1px';
		textarea.style.opacity = '0.01';
		document.body.appendChild(textarea);
		textarea.focus();
		textarea.select();
		textarea.setSelectionRange(0, textarea.value.length);
		const ok = document.execCommand('copy');
		document.body.removeChild(textarea);
		return ok;
	} catch {
		return false;
	}
}

function NoDevicePanel({
	canSwitch,
	onSwitch,
}: {
	canSwitch: boolean;
	onSwitch: () => void;
}) {
	const { t } = useTranslation();
	return (
		<div
			style={{
				margin: 16,
				background: '#fff',
				borderRadius: 10,
				padding: '28px 16px',
				textAlign: 'center',
				border: '1px solid #eceff3',
			}}
		>
			<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('player.no_device')} />
			{canSwitch && (
				<Button type="primary" onClick={onSwitch} style={{ marginTop: 8 }}>
					{t('player.no_device')}
				</Button>
			)}
		</div>
	);
}

function DateChipBtn({ active, onClick, label, outlined }: { active: boolean; onClick: () => void; label: string; outlined?: boolean }) {
	const baseColor = active ? PRIMARY : '#666';
	return (
		<span
			onClick={onClick}
			style={{
				display: 'inline-block',
				padding: '4px 10px',
				borderRadius: 6,
				border: outlined ? `1px solid ${active ? PRIMARY : '#d9d9d9'}` : 'none',
				color: baseColor,
				fontWeight: active ? 600 : 400,
				fontSize: 14,
				cursor: 'pointer',
				userSelect: 'none',
			}}
		>
			{label}
		</span>
	);
}

function FnButton({
	Icon,
	label,
	onClick,
	active,
	disabled,
	disabledMessage,
	onDisabled,
}: {
	Icon: React.ComponentType<{ style?: React.CSSProperties }>;
	label: string;
	onClick: () => void;
	active?: boolean;
	disabled?: boolean;
	disabledMessage?: string;
	onDisabled?: (message: string) => void;
}) {
	const opacity = disabled ? 0.4 : 1;
	const handleClick = () => {
		if (disabled) {
			if (disabledMessage) onDisabled?.(disabledMessage);
			return;
		}
		onClick();
	};
	return (
		<button
			type="button"
			aria-disabled={disabled}
			onClick={handleClick}
			style={{
				border: 0,
				background: 'transparent',
				padding: 0,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				gap: 8,
				cursor: disabled ? 'not-allowed' : 'pointer',
				opacity,
			}}
		>
			<div
				style={{
					width: 56,
					height: 56,
					borderRadius: '50%',
					background: active ? PRIMARY : '#fff',
					border: `1px solid ${active ? PRIMARY : '#e8e8e8'}`,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<Icon style={{ fontSize: 22, color: active ? '#fff' : '#444' }} />
			</div>
			<Typography.Text style={{ fontSize: 13, color: active ? PRIMARY : '#333' }}>{label}</Typography.Text>
		</button>
	);
}
